import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

/**
 * MarkdownView — themed renderer for Hypes PSI documents.
 * Uses GitHub-flavored markdown (tables, task lists, strikethrough).
 */
export default function MarkdownView({ source = "", compact = false }) {
  const proseSize = compact ? "prose-sm" : "prose";
  return (
    <div className={`hypes-md ${proseSize} max-w-none`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: (p) => <h1 className="text-3xl sm:text-4xl font-light text-[#1B291D] tracking-tight mt-2 mb-4" style={{ fontFamily: 'Work Sans' }} {...p} />,
          h2: (p) => <h2 className="text-2xl font-medium text-[#1B291D] tracking-tight mt-8 mb-3 pb-2 border-b border-[#E0E6E1]" style={{ fontFamily: 'Work Sans' }} {...p} />,
          h3: (p) => <h3 className="text-lg font-semibold text-[#1B291D] mt-6 mb-2" style={{ fontFamily: 'Work Sans' }} {...p} />,
          h4: (p) => <h4 className="text-sm font-bold text-[#43A047] tracking-wider uppercase mt-5 mb-2" {...p} />,
          p:  (p) => <p className="text-[14px] text-[#1B291D]/85 leading-relaxed mb-3" {...p} />,
          a:  ({ href, ...p }) => <a href={href} target="_blank" rel="noreferrer" className="text-[#43A047] underline underline-offset-2 hover:text-[#388E3C]" {...p} />,
          ul: (p) => <ul className="list-disc pl-6 mb-3 space-y-1 text-[14px] text-[#1B291D]/85" {...p} />,
          ol: (p) => <ol className="list-decimal pl-6 mb-3 space-y-1 text-[14px] text-[#1B291D]/85" {...p} />,
          li: (p) => <li className="leading-relaxed" {...p} />,
          strong: (p) => <strong className="font-semibold text-[#1B291D]" {...p} />,
          em: (p) => <em className="text-[#388E3C]" {...p} />,
          blockquote: (p) => <blockquote className="border-l-4 border-[#43A047] bg-[#43A047]/5 pl-4 pr-3 py-2 my-4 rounded-r text-[13px] text-[#1B291D]/80 italic" {...p} />,
          code: ({ inline, className, children, ...rest }) =>
            inline ? (
              <code className="px-1.5 py-0.5 rounded bg-[#1B291D]/5 text-[#1B291D] text-[12.5px] font-mono" {...rest}>{children}</code>
            ) : (
              <code className={`block bg-[#0E1A11] text-[#9CCC9F] p-3 rounded-lg text-[12.5px] font-mono whitespace-pre overflow-x-auto ${className || ""}`} {...rest}>{children}</code>
            ),
          pre: ({ children }) => <pre className="bg-[#0E1A11] text-[#9CCC9F] p-4 rounded-xl overflow-x-auto mb-4 text-[12.5px] leading-relaxed">{children}</pre>,
          table: (p) => <div className="overflow-x-auto my-4"><table className="min-w-full border border-[#E0E6E1] rounded-lg overflow-hidden text-sm" {...p} /></div>,
          thead: (p) => <thead className="bg-[#43A047]/10 text-[#1B291D]" {...p} />,
          th: (p) => <th className="text-left px-3 py-2 text-[12px] font-bold text-[#1B291D] uppercase tracking-wider border-b border-[#E0E6E1]" {...p} />,
          td: (p) => <td className="px-3 py-2 text-[13px] text-[#1B291D]/85 border-b border-[#E0E6E1]" {...p} />,
          hr: () => <hr className="my-8 border-t border-[#E0E6E1]" />,
          img: (p) => <img className="rounded-lg my-3 max-w-full" {...p} />,
        }}
      >
        {source}
      </ReactMarkdown>
    </div>
  );
}
